import React from 'react';
import Slider, { Settings } from 'react-slick';
import bemBlock from '@gik/core/utils/bemBlock';
import { UISize, UIVariant } from '@gik/ui/types';
//import SvgIcon from '../SvgIcon';
import { ArrowButtonNext, ArrowButtonPrev } from '../ArrowButton';

export interface ICarouselProps extends Settings {
  children?: React.ReactNode;
  variant?: UIVariant;
  arrowButtonSize?: UISize;
  //arrowsVariant?: UIVariant;
}

export const defaultSlideAnimationSpeed = 500;

/**
 * @see https://react-slick.neostack.com/docs/api
 */
function Carousel(
  {
    children,
    dots = true,
    arrows = true,
    className,
    slidesPerRow = 1,
    variant = 'default',
    //arrowsVariant,
    arrowButtonSize,
    ...sliderProps
  }: ICarouselProps,
  ref: React.LegacyRef<Slider>
): React.ReactElement {
  const slides = getValidChildren(children);
  const bem = bemBlock('carousel');
  const innerRef = React.useRef(null);
  const combinedRef = useCombinedRefs<Slider>(ref, innerRef);
  const [slideNumber, setSlideNumber] = React.useState<number>(0);

  function getValidChildren(children: React.ReactNode) {
    return React.Children.toArray(children).filter(child => React.isValidElement(child)) as React.ReactElement[];
  }

  const hasPagination = slides.length > slidesPerRow;

  const isPrevButtonDisabled = sliderProps?.infinite === false && slideNumber === 0;

  const isNextButtonDisabled =
    sliderProps?.infinite === false && slideNumber >= slides.length - (sliderProps?.slidesToShow || 1);

  const portalledDots = dots => {
    return <ul id="bla">{dots}</ul>;
  };

  const sliderClasses = bem('slider', [
    { 'has-dots': dots && hasPagination },
    { 'has-arrows': arrows && hasPagination },
  ]);

  const props: Partial<ICarouselProps> = {
    ...sliderProps,
    slidesPerRow,
    arrows: false,
    dots,
    appendDots: portalledDots,
    speed: defaultSlideAnimationSpeed,
    className: sliderClasses,
    beforeChange: (currentSlide: number, nextSlide: number) => {
      setSlideNumber(nextSlide);
      sliderProps.beforeChange?.(currentSlide, nextSlide);
    },
  };

  return (
    <div className={bem(null, [{ [variant]: variant }], className)}>
      <Slider ref={combinedRef} {...props}>
        {children}
      </Slider>
      {arrows && hasPagination && (
        <ArrowButtonNext
          className={bem('arrow-next')}
          disabled={isNextButtonDisabled}
          onClick={() => combinedRef?.current.slickNext()}
          size={arrowButtonSize}
        />
      )}
      {arrows && hasPagination && (
        <ArrowButtonPrev
          className={bem('arrow-prev')}
          disabled={isPrevButtonDisabled}
          onClick={() => combinedRef?.current.slickPrev()}
          size={arrowButtonSize}
        />
      )}
    </div>
  );
}

// We need a reference to slick in our core carousel component, but also forward it to any component that uses our carousel
// see https://itnext.io/reusing-the-ref-from-forwardref-with-react-hooks-4ce9df693dd
// TODO: move this into our shared hooks
function useCombinedRefs<T>(...refs) {
  const targetRef = React.useRef();

  React.useEffect(() => {
    refs.forEach(ref => {
      if (!ref) return;

      if (typeof ref === 'function') {
        ref(targetRef.current);
      } else {
        ref.current = targetRef.current;
      }
    });
  }, [refs]);

  return targetRef as React.MutableRefObject<T>;
}

export default React.forwardRef(Carousel);
